// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Connect your agents" (design/konvo-agents/c-connect.png). Konvo makes a one-time code
// and the command for it (`npx -y <Konvo>/konvo/agents/install/<code>/konvo-agents.tgz connect
// <code>`: Konvo serves the bridge itself, so there is no registry setup); the person runs it
// where the agents run, and the dialog waits for the machine (GET /konvo/agents every 3 s plus the
// realtime "machines" event) and then says which machine connected.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {CommandBlock, useNow} from '@app/features/konvo_agents/components/AgentBits';
import {SupportedToolChips} from '@app/features/konvo_agents/components/AgentBrand';
import {AgentSetup} from '@app/features/konvo_agents/components/AgentSetup';
import styles from '@app/features/konvo_agents/components/AgentsConnectModal.module.css';
import {agentErrorMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import Agents from '@app/features/konvo_agents/state/Agents';
import {formatCountdown, secondsLeft} from '@app/features/konvo_agents/state/AgentsModel';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import type {AgentMachineView, AgentPairing} from '@fluxer/schema/src/domains/konvo/Agents';
import {plural} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconCheck, IconLock, IconTerminal2} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef, useState} from 'react';

const MODAL_KEY = 'konvo-agents-connect';
const POLL_MS = 3000;

/** The command Konvo gives with the code; the registry form only if an older API left it out. */
export function connectCommand(pairing: AgentPairing): string {
	return pairing.command;
}

/** The machine that connected since the dialog opened, if any. */
function newMachine(
	machines: ReadonlyArray<AgentMachineView>,
	knownIds: ReadonlySet<string>,
	since: number,
): AgentMachineView | null {
	return (
		machines.find((machine) => !knownIds.has(machine.id)) ??
		machines.find((machine) => Date.parse(machine.connectedAt) >= since) ??
		null
	);
}

export const AgentsConnectModal = observer(({onClose, machine}: {onClose: () => void; machine?: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [pairing, setPairing] = useState<AgentPairing | null>(null);
	const [pairingError, setPairingError] = useState<string | null>(null);
	const [requesting, setRequesting] = useState(false);
	const [helpOpen, setHelpOpen] = useState(false);
	const knownIds = useRef<Set<string>>(new Set(Agents.machines.map((machine) => machine.id)));
	const since = useRef(Date.now());
	const now = useNow(1000);

	const requestCode = useCallback(async () => {
		setRequesting(true);
		setPairingError(null);
		try {
			const next = await Agents.createPairing();
			since.current = Date.now() - 5000;
			setPairing(next);
		} catch (error) {
			setPairingError(agentErrorMessage(i18n, error));
		} finally {
			setRequesting(false);
		}
	}, [i18n]);

	useEffect(() => {
		void requestCode();
	}, [requestCode]);

	const connected =
		(machine && Agents.machines.find((entry) => entry.id === machine.id && entry.online)) ||
		newMachine(Agents.machines, knownIds.current, since.current);

	// Wait for the machine: poll while open (the realtime "machines" event refreshes too).
	useEffect(() => {
		if (connected || !pairing) return;
		const timer = window.setInterval(() => void Agents.loadOverview(), POLL_MS);
		return () => window.clearInterval(timer);
	}, [connected, pairing]);

	const left = pairing ? secondsLeft(pairing.expiresAt, now) : 0;
	const expired = Boolean(pairing) && left === 0 && !connected;
	const tool = connected?.tools[0];
	const projectCount = connected?.projects.length ?? 0;
	const machineName = connected?.name ?? '';
	const reconnectName = machine?.name ?? '';
	const countdown = formatCountdown(left);

	return (
		<Modal.Root size="medium" centered onClose={onClose} data-flx="konvo-agents.connect.root">
			<Modal.Header
				icon={
					<span className={styles.headerTile}>
						<IconTerminal2 size={22} stroke={1.6} />
					</span>
				}
				title={machine ? t`Reconnect ${reconnectName}` : t`Connect your agents`}
				onClose={onClose}
				data-flx="konvo-agents.connect.header"
			/>
			<Modal.Content data-flx="konvo-agents.connect.content">
				<div className={styles.content}>
					<p className={styles.lead}>
						<Trans>Let Konvo see your agent chats and, when you confirm, send them prompts.</Trans>
					</p>
					<SupportedToolChips />

					<ol className={styles.steps}>
						<li className={styles.step}>
							<span className={styles.num}>1</span>
							<div className={styles.body}>
								<span className={styles.stepTitle}>
									<Trans>Run this where your agents run</Trans>
								</span>
								<p className={styles.hint}>
									{expired ? (
										<Trans>This code has expired. Get a new one to connect.</Trans>
									) : pairing ? (
										<Trans>
											On the computer or server where Claude Code or Codex runs. Konvo works through T3 Code, and the
											command sets it up if it isn't there yet. The code works once and expires in {countdown}.
										</Trans>
									) : (
										<Trans>
											On the computer or server where Claude Code or Codex runs. Konvo works through T3 Code, and the
											command sets it up if it isn't there yet.
										</Trans>
									)}
								</p>
								<p className={styles.hint}>
									<Trans>
										Signed-in Claude Code and Codex accounts are detected and enabled automatically. If a sign-in is
										missing or expired, sign in once on that computer.
									</Trans>
								</p>
								{pairingError ? (
									<div className={styles.errorRow} role="alert">
										<span>{pairingError}</span>
										<Button variant="secondary" small onClick={() => void requestCode()} submitting={requesting}>
											<Trans>Try again</Trans>
										</Button>
									</div>
								) : pairing && !pairing.command ? (
									<p className={styles.errorRow} role="alert">
										<Trans>This server cannot provide the connection installer. Ask the Konvo team to update it.</Trans>
									</p>
								) : pairing ? (
									<div className={clsx(expired && styles.expired)}>
										<CommandBlock
											lines={[connectCommand(pairing)]}
											highlight={pairing.code}
											wrap
											data-flx="konvo-agents.connect.command"
										/>
									</div>
								) : (
									<div className={styles.placeholder} aria-busy="true" />
								)}
								{expired && (
									<Button
										variant="secondary"
										small
										fitContent
										onClick={() => void requestCode()}
										submitting={requesting}
										data-flx="konvo-agents.connect.new-code"
									>
										<Trans>New code</Trans>
									</Button>
								)}
							</div>
						</li>

						<li className={styles.step}>
							<span className={clsx(styles.num, connected && styles.numDone)}>
								{connected ? <IconCheck size={14} stroke={2.4} /> : '2'}
							</span>
							<div className={styles.body}>
								<span className={styles.stepTitle}>
									<Trans>Choose what Konvo may see</Trans>
								</span>
								<p className={styles.hint}>
									<Trans>
										The command asks which projects to share and whether Konvo may send prompts. You can change it later
										on that machine.
									</Trans>
								</p>
								{connected ? (
									<div className={clsx(styles.waiting, styles.connected)} data-flx="konvo-agents.connect.connected">
										<span className={styles.connectedTick}>
											<IconCheck size={14} stroke={2.6} />
										</span>
										<span className={styles.waitingText}>
											<Trans>Connected: {machineName}</Trans>
											{tool ? ` · ${tool.name} ${tool.version}` : ''}
											{' · '}
											{plural(projectCount, {one: '# project', other: '# projects'})}
										</span>
									</div>
								) : (
									<div className={styles.waiting} data-flx="konvo-agents.connect.waiting">
										<span className={styles.spinner} aria-hidden="true" />
										<span className={styles.waitingText}>
											<Trans>Waiting for your machine…</Trans>
										</span>
										<button
											type="button"
											className={styles.helpButton}
											aria-expanded={helpOpen}
											onClick={() => setHelpOpen((open) => !open)}
											data-flx="konvo-agents.connect.help"
										>
											<Trans>Not connecting?</Trans>
										</button>
									</div>
								)}
								{helpOpen && !connected && (
									<ul className={styles.help} data-flx="konvo-agents.connect.help-list">
										<li>
											<Trans>Use Node.js 22.16 or newer.</Trans>
										</li>
										<li>
											<Trans>If the download fails, the code may have expired: get a new one.</Trans>
										</li>
										<li>
											<Trans>
												The setup command prints the exact command for checking this connection. Use that command on the
												same computer.
											</Trans>
										</li>
										<li>
											<Trans>The machine needs to reach Konvo over HTTPS (port 443 out). Nothing comes in.</Trans>
										</li>
									</ul>
								)}
							</div>
						</li>
					</ol>
					{connected && <AgentSetup machine={connected} compact />}
				</div>
			</Modal.Content>
			<Modal.Footer data-flx="konvo-agents.connect.footer">
				<p className={styles.fineprint}>
					<IconLock size={15} stroke={1.7} />
					<Trans>Your machine only connects out to Konvo. Nothing is opened to the internet.</Trans>
				</p>
				{connected ? (
					<Button onClick={onClose} data-flx="konvo-agents.connect.done">
						<Trans>Done</Trans>
					</Button>
				) : (
					<Button variant="secondary" onClick={onClose} data-flx="konvo-agents.connect.cancel">
						<Trans>Cancel</Trans>
					</Button>
				)}
			</Modal.Footer>
		</Modal.Root>
	);
});

export function openAgentsConnectModal(): void {
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => <AgentsConnectModal onClose={() => ModalCommands.popWithKey(MODAL_KEY)} />),
		MODAL_KEY,
	);
}

export function openAgentsReconnectModal(machine: AgentMachineView): void {
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => (
			<AgentsConnectModal machine={machine} onClose={() => ModalCommands.popWithKey(MODAL_KEY)} />
		)),
		MODAL_KEY,
	);
}
